import moment from 'moment'
import _ from 'lodash'
import { ResponseCode } from '~/utils/constant'
import { batchProcessRequests } from '~/utils/common'
import { fetchExhibitionPage } from '~~/server/api/exhibition'
import { fetchIndustryGetAll } from '~~/server/api/newsApi'
import { fetchNewsHomePageList } from '~~/server/api/newsApi'
import { ELocale } from '~~/types/enums'
import { fetchExhibitionAliasList } from '~~/server/api/exhibition'

export const useNews = async (_lang: ELocale) => {
  const route = useRoute()
  const router = useRouter()
  const { lang, $t } = useLang(_lang)

  // t 默认应该为1(t 资讯类型 newsType, i行业id)
  const { t = '1', i = '0', page = '1' } = route.params

  let industry: Record<string, any>[] = []
  let news: Record<string, any>[] = []
  let secondNews: Record<string, any>[] = []
  let newsTotal = 0
  let fair: Record<string, any>[] = []
  let tags = [$t('news.notMultiple')]
  let newsType = '0'
  let industryId = '0'
  let pageNum = 1
  const pageSize = 15 // { value: 15 }
  let exhibitionIdList = { value: [] }

  pageNum = isNaN(+page) ? 1 : +page

  // Filters
  const formatIndustry = (val: string): string => {
    if (!val) return ''
    const vals = val.split('/')
    return vals.slice(0, 2).join('/')
  }

  const subIndustry = (idx: number) => {
    const index = idx % 5
    switch (index) {
      case 0:
        return 'transform: translateX(-8%);'
      case 1:
        return 'transform: translateX(-16%);'
      case 2:
        return 'transform: translateX(-42%);'
      case 3:
        return 'transform: translateX(-22%);'
      case 4:
        return 'transform: translateX(-58%);'
    }
  }

  const { data } = await useAsyncData('newsPageData', async () => {
    // 资讯类型： 0:展会资讯 industry 1:行业动态 industryNews 2:展会政策 policy 3:展会知识 knowledge
    const listParams: Record<string, any> = {
      locale: lang,
      // newsType: t,
      pageNum: pageNum > 1 && i === '0' ? pageNum + 1 : pageNum,
      pageSize: pageSize
    }

    if (+i !== 0) {
      listParams.industryId = +i
    }

    const fetchList = [
      // 行业分类数据 - 字段级优化
      async () => {
        const res = await fetchIndustryGetAll({ locale: lang })
        if (res?.code === ResponseCode.SUCCESS) {
          return {
            ...res,
            data: (res?.data || []).map((item: any) => ({
              industryId: item.industryId,
              industryName: item.industryName,
              icon: item.icon,
              children: (item.children || []).map((child: any) => ({
                id: child.id,
                pid: child.pid,
                industryName: child.industryName
              }))
            }))
          }
        }
        return res
      },
      // 新闻列表数据 - 字段级优化
      async () => {
        const res = await fetchNewsHomePageList(listParams)
        if (res?.code === ResponseCode.SUCCESS) {
          return {
            ...res,
            data: {
              ...res?.data,
              total: res?.data?.total || 0,
              pages: res?.data?.pages || 0,
              records: (res?.data?.records || []).map((item: any) => ({
                newsId: item.newsId,
                viewUrl: item.viewUrl,
                newsName: item.newsName,
                logo: item.logo,
                industryName: item.industryName,
                industryId: item.industryId,
                publishTime: item.publishTime,
                views: item.views,
                description: item.description,
                specialTopics: item.specialTopics
              }))
            }
          }
        }
        return res
      },
      // 展会数据 - 字段级优化
      async () => {
        const res = await fetchExhibitionPage({
          locale: lang,
          pageNum: 1,
          pageSize: 15
        })
        if (res?.code === ResponseCode.SUCCESS) {
          return {
            ...res,
            data: {
              ...res?.data,
              records: (res?.data?.records || []).map((item: any) => ({
                exhibitionId: item.exhibitionId,
                exhibitionName: item.exhibitionName,
                exhibitionEnglishName: item.exhibitionEnglishName,
                pic: item.pic,
                logo: item.logo,
                lunchTime: item.lunchTime,
                changeTime: item.changeTime,
                area: item.area,
                areaName: item.areaName,
                hot: item.hot,
                status: item.status,
                countDays: item.countDays,
                dynamic: item.dynamic,
                exhibitionFollows: item.exhibitionFollows || [],
                aliasPrimary: item.aliasPrimary,
                exhibitionAlias: item.exhibitionAlias
              }))
            }
          }
        }
        return res
      }
    ]

    if (+i === 0) {
      fetchList.push(
        // 第二页新闻数据 - 字段级优化
        async () => {
          const res = await fetchNewsHomePageList({
            ...listParams,
            pageNum: 2
          })
          if (res?.code === ResponseCode.SUCCESS) {
            return {
              ...res,
              data: {
                ...res?.data,
                total: res?.data?.total || 0,
                pages: res?.data?.pages || 0,
                records: (res?.data?.records || []).map((item: any) => ({
                  newsId: item.newsId,
                  viewUrl: item.viewUrl,
                  newsName: item.newsName,
                  logo: item.logo,
                  industryName: item.industryName,
                  industryId: item.industryId,
                  publishTime: item.publishTime,
                  views: item.views,
                  description: item.description,
                  specialTopics: item.specialTopics
                }))
              }
            }
          }
          return res
        }
      )
    }

    return await batchProcessRequests(fetchList, { parallel: true })
  })

  const [industryRes, newsRes, newFairRes, secondRes] = data.value || []
  if (industryRes?.code === ResponseCode.SUCCESS) {
    industry = industryRes.data
  }

  if (newsRes?.code === ResponseCode.SUCCESS && newsRes?.data?.records) {
    newsTotal = newsRes.data.total

    // 优化时间格式化，避免重复计算
    const timeFormat = +i > 0 || pageNum.value > 1 ? 'MM-DD HH:mm:ss' : 'HH:mm:ss'

    // 直接在原始数据上添加必要字段，减少对象创建
    news = newsRes.data.records.map((item: any) => ({
      newsId: item.newsId,
      viewUrl: item.viewUrl,
      newsName: item.newsName,
      logo: item.logo,
      industryName: item.industryName,
      industryId: item.industryId,
      createTime: moment(item.publishTime).format(timeFormat),
      views: item.views,
      description: item.description ? `${item.description}...` : '',
      specialTopics: item.specialTopics
    }))

    if (pageNum > 1 && pageNum > newsRes.data.pages) {
      throw createError({
        statusCode: 404,
        statusMessage: 'Page not found',
        message: route.fullPath
      })
    }
  }
  if (newFairRes?.code === ResponseCode.SUCCESS && newFairRes.data?.records) {
    const records = newFairRes.data.records

    // 一次性收集所有展会ID，避免重复push操作
    exhibitionIdList.value = records.map((item: any) => item.exhibitionId)

    // 只有在有展会数据时才请求别名信息
    if (records.length > 0) {
      const { data: aliasRes } = await useAsyncData('news-exhibition-alias-data', async () => {
        const res = await fetchExhibitionAliasList({
          exhibitionIdList: exhibitionIdList.value
        })
        if (res?.code === ResponseCode.SUCCESS) {
          return {
            ...res,
            data: (res?.data || []).map((item: any) => ({
              exhibitionId: item.exhibitionId,
              exhibitionAlias: item.exhibitionAlias,
              isPrimary: item.isPrimary
            }))
          }
        }
        return res
      })

      if (aliasRes.value?.code === ResponseCode.SUCCESS) {
        const aliasList = aliasRes.value.data || []

        // 创建别名映射以提高查找效率
        const aliasMap = new Map()
        aliasList.forEach((alias: any) => {
          if (alias.isPrimary === 1) {
            aliasMap.set(alias.exhibitionId, alias.exhibitionAlias)
          }
        })

        // 优化数据处理，减少对象创建
        records.forEach((item: any) => {
          const alias = aliasMap.get(item.exhibitionId)
          item.exhibitionAlias = alias || ''
          item.nameTitle = alias || ''
        })
      }
    }

    fair = records
  }
  if (secondRes?.code === ResponseCode.SUCCESS && secondRes.data?.records) {
    // 优化 secondNews 数据处理，减少对象创建
    secondNews = secondRes.data.records.map((item: any) => ({
      newsId: item.newsId,
      viewUrl: item.viewUrl,
      newsName: item.newsName,
      logo: item.logo,
      industryName: item.industryName,
      industryId: item.industryId,
      createTime: moment(item.publishTime).format('MM-DD HH:mm:ss'),
      views: item.views,
      description: item.description ? `${item.description}...` : '',
      specialTopics: item.specialTopics
    }))
  }

  // methods
  const handleClick = (tab: any) => {
    let path = `/information-${tab.name}-${industryId}-1/`
    if (tab.name === '0' && industryId === '0') {
      path = '/information/'
    }
    router.push({
      path
    })
  }

  const $get = _.get

  return {
    industry,
    news,
    secondNews,
    newsTotal,
    fair,
    tags,
    newsType: t,
    industryId: i,
    pageNum,
    pageSize,
    formatIndustry,
    subIndustry,
    handleClick,
    $t,
    $get
  }
}
